import React, { useEffect, useState } from "react";
import { TextField } from "@merge-api/merge-javascript-shared";
import { customNotEquals } from "./utils";
import useCustomizationContext from "../context/useCustomizationContext";
import { MergeLinkCustomizationStatus } from "../context/CustomizationContext";

// Regex for CSS border-radius values including units, keywords, and calc()
const BORDER_RADIUS_REGEX =
  /^(?:\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|vmin|vmax|ch|ex|in|cm|mm|pt|pc)?|inherit|initial|unset|none|auto|calc\(.+\))(?:\s+\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|vmin|vmax|ch|ex|in|cm|mm|pt|pc)?){0,3}(?:\s*\/\s*\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|vmin|vmax|ch|ex|in|cm|mm|pt|pc)?(?:\s+\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|vmin|vmax|ch|ex|in|cm|mm|pt|pc)?){0,3})?$/;

export enum BorderRadiusFieldEnum {
  BUTTON = "button_border_radius",
  MODAL = "modal_border_radius",
  SELECTION_BUTTON = "selection_button_border_radius",
}

type CustomizationBorderRadiusRowProps = {
  type: BorderRadiusFieldEnum;
  label: string;
  className?: string;
};

const CustomizationBorderRadiusRow = ({
  type,
  label,
  className,
}: CustomizationBorderRadiusRowProps) => {
  const [borderRadiusValue, setBorderRadiusValue] = useState<string | null>(null);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const { organizationCustomization, setOrganizationCustomization } = useCustomizationContext();
  const currBorderRadius = organizationCustomization?.[type] ?? null;

  useEffect(() => {
    setBorderRadiusValue(currBorderRadius);
    setIsInvalid(false); // Reset error state when value changes from context
  }, [organizationCustomization, currBorderRadius]);

  const updateOrganizationCustomization = () => {
    // Don't save if the value is invalid
    if (isInvalid) {
      return;
    }

    if (organizationCustomization != undefined) {
      if (!customNotEquals(currBorderRadius, borderRadiusValue)) {
        return;
      }
      const newOrgCustomization = { ...organizationCustomization };
      // Set empty string if borderRadiusValue is null or empty, otherwise use the value
      newOrgCustomization[type] = borderRadiusValue || "";
      setOrganizationCustomization(newOrgCustomization);
    } else {
      // Create new organization customization even with empty values
      const newOrgCustomization = {
        status: "PUBLISHED" as MergeLinkCustomizationStatus,
        [type]: borderRadiusValue || "",
      };
      setOrganizationCustomization(newOrgCustomization);
    }
  };

  return (
    <div className="flex flex-row items-center justify-between h-[26px]">
      <div className={`text-base ${className || ""}`}>{label}</div>
      <div className="w-36">
        <TextField
          onChange={(e) => {
            const value = e.target.value;

            // Always update the display value
            setBorderRadiusValue(value || null);

            // Validate and set error state
            if (value !== "" && !BORDER_RADIUS_REGEX.test(value)) {
              setIsInvalid(true);
            } else {
              setIsInvalid(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key == "Enter") {
              updateOrganizationCustomization();
            }
          }}
          onBlur={() => updateOrganizationCustomization()}
          placeholder="12px"
          value={borderRadiusValue ?? ""}
          inputClassName="max-h-[26px]"
          error={isInvalid}
        />
      </div>
    </div>
  );
};

export default CustomizationBorderRadiusRow;
